<template>
  <ModalDefault id="view-anexo" title="Anexo" size="lg" :show-footer="false" :fnc-on-close="() => emit('close')">
    <template #modal-body>
      <div v-if="loading" class="flex justify-center py-8">
        <span class="text-gray-500">Cargando anexo...</span>
      </div>
      <div v-else-if="!anexos.length" class="flex justify-center py-8">
        <span class="text-gray-500">No hay anexos relacionados.</span>
      </div>
      <div v-else>
        <div class="flex items-center justify-between mb-4">
          <div v-if="anexos.length > 1" class="flex items-center gap-x-3">
            <button
              type="button"
              class="inline-flex items-center gap-x-1 text-xs text-gray-600 hover:text-blue-600 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
              :disabled="currentIdx === 0"
              @click="onPrev"
            >
              <ChevronLeftIcon class="size-4" />
              Anterior
            </button>
            <span class="text-xs text-gray-500">{{ currentIdx + 1 }} / {{ anexos.length }}</span>
            <button
              type="button"
              class="inline-flex items-center gap-x-1 text-xs text-gray-600 hover:text-blue-600 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
              :disabled="currentIdx === anexos.length - 1"
              @click="onNext"
            >
              Siguiente
              <ChevronRightIcon class="size-4" />
            </button>
          </div>
        </div>
        <template v-if="isPdf">
          <object>
            <embed
              type="text/html"
              width="100%"
              height="700"
              :src="'data:application/pdf;base64,' + currentItem.base64"
            />
          </object>
        </template>
        <template v-else-if="currentItem.base64">
          <img
            :src="currentItem.base64"
            class="rounded-xl"
            width="100%"
            height="100%"
          />
        </template>
      </div>
    </template>
  </ModalDefault>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, nextTick } from 'vue'
import { ChevronLeftIcon, ChevronRightIcon } from '@heroicons/vue/24/outline'
import { openModalById } from '@/modules/Core/composables/useModal'
import type { IAnexoRelacion, ICitaAnexo } from '@/modules/Asistencial/interfaces'
import { anexoService } from '@/services'
import ModalDefault from '@/modules/Core/components/Layout/ModalDefault.vue'

const props = defineProps<{
  cittId?: number
  anexosRelaciones?: IAnexoRelacion[]
}>()

const emit = defineEmits<{
  close: []
}>()

const loading = ref(true)
const anexos = ref<ICitaAnexo[]>([])
const currentIdx = ref(0)
const currentItem = computed(() => anexos.value[currentIdx.value] || {} as ICitaAnexo)
const isPdf = computed(() => {
  const b64 = currentItem.value.base64
  return !!b64 && !b64.startsWith('data:')
})

async function loadCurrentItem (idx: number) {
  if (!props.anexosRelaciones?.length) return
  const rel = props.anexosRelaciones[idx]
  if (!rel) return
  window.notificacion.loading().circle()
  const { data } = await anexoService.getAnexe(rel.cianId)
  anexos.value[idx] = { ...anexos.value[idx], base64: data.body as string }
  window.notificacion.loading().remove()
}

async function onPrev () {
  if (currentIdx.value === 0) return
  currentIdx.value--
  if (!anexos.value[currentIdx.value]?.base64) {
    loading.value = true
    await loadCurrentItem(currentIdx.value)
    loading.value = false
  }
}

async function onNext () {
  if (currentIdx.value === anexos.value.length - 1) return
  currentIdx.value++
  if (!anexos.value[currentIdx.value]?.base64) {
    loading.value = true
    await loadCurrentItem(currentIdx.value)
    loading.value = false
  }
}

onMounted(async () => {
  try {
    if (props.anexosRelaciones?.length) {
      anexos.value = props.anexosRelaciones.map(r => ({ cianId: r.cianId }) as ICitaAnexo)
      await loadCurrentItem(0)
    }
  } catch (error) {
    console.error('Error al cargar anexos:', error)
    anexos.value = []
  } finally {
    loading.value = false
    await nextTick()
    openModalById('view-anexo', true, () => emit('close'))
  }
})
</script>
